/**
 * Validation for the catalogue, quotations and pricing agreements.
 *
 * READ CLAUDE.md SECTION 4 FIRST. There is deliberately no schema in this file
 * for a test input, a reading, a unit of measurement, a formula, a rounding
 * rule, an acceptance limit, a chart or an Excel mapping — for any test,
 * including the five initial ones. A catalogue entry is validated as a
 * COMMERCIAL record: a name, a published standard it is performed to, a
 * material, a turnaround, a price and a way of charging it.
 *
 * THE SECOND GOVERNING IDEA IS THAT AN EMPTY FIELD IS NEVER ZERO. A base price
 * is `null` until somebody sets one, and `null` is the default. A schema that
 * defaulted it to `0` would make every uncosted service look like a free one,
 * which is a false statement about a laboratory's own price list.
 *
 * The backend re-validates every one of these. The frontend uses them so the
 * user hears about a problem before the round trip.
 */
import { z } from 'zod';
import { ALL_DURATION_UNITS, ALL_PRICE_SOURCES, ALL_PRICING_UNITS, ALL_QUOTATION_SEND_CHANNELS, ALL_QUOTATION_STORED_STATUSES, ALL_TEMPLATE_READINESS, ALL_LOCALES, } from '@lims/shared-types';
import { documentWordingSchema } from './organisation.js';
import { sampleTypeCodeSchema } from './sample-types.js';
import { bilingualTextSchema, businessTextSchema, optionalBusinessText, isoDateSchema, listQuerySchema, moneyMinorSchema, reasonSchema, uuidSchema, } from './primitives.js';
// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------
/**
 * A catalogue entry's sample type: a CODE of the laboratory's own types
 * (D306). Only its shape is checked here; whether this laboratory has it, and
 * whether it is archived, is the API's decision.
 */
export const sampleTypeSchema = sampleTypeCodeSchema;
export const pricingUnitSchema = z.enum(ALL_PRICING_UNITS);
export const durationUnitSchema = z.enum(ALL_DURATION_UNITS);
export const templateReadinessSchema = z.enum(ALL_TEMPLATE_READINESS);
export const quotationStoredStatusSchema = z.enum(ALL_QUOTATION_STORED_STATUSES);
export const quotationSendChannelSchema = z.enum(ALL_QUOTATION_SEND_CHANNELS);
export const priceSourceSchema = z.enum(ALL_PRICE_SOURCES);
export const reportLanguagesSchema = z
    .array(z.enum(ALL_LOCALES))
    .min(1)
    .max(ALL_LOCALES.length);
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    // An empty string is an absent value, not a value of "". Storing "" would
    // make an empty field look like a recorded fact.
    .transform((value) => (value === '' ? null : value));
/**
 * A price in minor units, or explicitly nothing.
 *
 * `null` is UNPRICED. It is not zero, it does not become zero, and a service
 * with no price is quoted only after somebody types one. A price that IS set
 * must be positive: a base price of zero on a price list asserts that this
 * laboratory performs the test for nothing, which is a claim nobody made
 * (PLAN.md, Phase 5 acceptance criteria).
 */
export const basePriceMinorSchema = moneyMinorSchema
    .positive({ message: 'A base price of zero would assert a price nobody set' })
    .nullable()
    .optional();
// ---------------------------------------------------------------------------
// Test categories
// ---------------------------------------------------------------------------
const categoryCoreSchema = z.object({
    /**
     * The laboratory's own short code. Optional: many laboratories group by name
     * alone, and demanding a code would mean inventing one.
     */
    code: optionalText(32),
    name: bilingualTextSchema,
    description: bilingualTextSchema.optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const createTestCategorySchema = categoryCoreSchema;
export const updateTestCategorySchema = categoryCoreSchema.partial().extend({
    /** Categories are deactivated, never deleted: past quotations refer to them. */
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
});
// ---------------------------------------------------------------------------
// Catalogue entries
// ---------------------------------------------------------------------------
/**
 * A duration is a value and a unit, or neither.
 *
 * Half of one is meaningless — "3" of what? — so the pair is refused unless
 * both sides are present or both absent.
 */
const durationIsPaired = (value) => (value.expectedDurationValue === undefined ||
    value.expectedDurationValue === null) ===
    (value.expectedDurationUnit === undefined ||
        value.expectedDurationUnit === null);
/**
 * A piece of equipment this entry needs, named in the laboratory's own words.
 *
 * FREE TEXT, deliberately. The equipment register arrives in Phase 8 and the
 * calibration rules with it; until then a laboratory records what it uses, and
 * this product does not pretend to know which apparatus any test requires —
 * that is precisely the detail CLAUDE.md section 4 refuses to invent. Phase 8
 * links these to real register entries.
 */
const requiredEquipmentSchema = z
    .array(bilingualTextSchema)
    .max(20)
    .optional();
const catalogueItemCoreSchema = z.object({
    /** The laboratory's own catalogue code, unique within the laboratory. */
    code: optionalText(32),
    name: bilingualTextSchema,
    categoryId: uuidSchema.nullable().optional(),
    /**
     * Which part of the laboratory performs it, as a REFERENCE to the
     * laboratory's own department register.
     *
     * Phase 5 held this as free text and said why: the register did not exist,
     * and a foreign key to a missing table would have left the column unusable
     * (decision D49). Phase 6 built the register, and migration 0005 turned
     * every distinct text a laboratory had typed into a row of its own — so
     * nothing entered was discarded, and one fact now has one home.
     */
    departmentId: uuidSchema.nullable().optional(),
    /**
     * The published standard, as a designation: `ASTM D2216`, `AASHTO T 89`,
     * `BS 1377-2`. A LABEL, recorded so a quotation and a report can cite what
     * the client asked for. Nothing in this product reads it, derives anything
     * from it, or implements it.
     */
    referenceStandard: optionalText(64),
    /** The edition or year of that standard: `19`, `2021`, `2012 (2021)`. */
    standardVersion: optionalText(32),
    sampleType: sampleTypeSchema.nullable().optional(),
    expectedDurationValue: z.number().int().positive().max(9999).nullable().optional(),
    expectedDurationUnit: durationUnitSchema.nullable().optional(),
    /** Null means UNPRICED. It never means free. */
    basePriceMinor: basePriceMinorSchema,
    pricingUnit: pricingUnitSchema.optional(),
    requiredEquipment: requiredEquipmentSchema,
    /** Which languages this laboratory can issue the result in. */
    reportLanguages: reportLanguagesSchema.optional(),
    notes: optionalText(4000),
});
/**
 * Changing a base price is a priced act: it requires `pricing.override` on top
 * of `catalogue.manage`, enforced by the API. A reason is OPTIONAL (D189): a
 * routine price-list edit needs none, and one that is given is recorded with
 * the audited change.
 */
export const createCatalogueItemSchema = catalogueItemCoreSchema
    .extend({
    priceReason: reasonSchema.optional(),
})
    .refine(durationIsPaired, {
    message: 'An expected duration needs both a value and a unit',
    path: ['expectedDurationUnit'],
});
export const updateCatalogueItemSchema = catalogueItemCoreSchema
    .partial()
    .extend({
    /** Entries are deactivated, never deleted: past quotations cite them. */
    isActive: z.boolean().optional(),
    priceReason: reasonSchema.optional(),
    reason: reasonSchema.optional(),
    /**
     * Template readiness is settable — a test moves from `not_ready` towards
     * `ready` as Phase 13 delivers it — but the API refuses to move it to
     * `ready` without the validated worked example the entry gate demands.
     * The schema's job is only to reject a value that is not one of the four.
     */
    templateReadiness: templateReadinessSchema.optional(),
    resultTemplateVersion: optionalText(32),
    reportTemplateReference: optionalText(64),
    /**
     * The report template this test's reports use instead of the laboratory
     * default (D254). Presentation only: it is not gated on readiness, and
     * `null` returns the test to the default.
     */
    reportTemplateId: uuidSchema.nullable().optional(),
})
    .refine(durationIsPaired, {
    message: 'An expected duration needs both a value and a unit',
    path: ['expectedDurationUnit'],
});
export const listCatalogueQuerySchema = listQuerySchema.extend({
    categoryId: uuidSchema.optional(),
    sampleType: sampleTypeSchema.optional(),
    templateReadiness: templateReadinessSchema.optional(),
    /** Inactive entries are listed by default and marked, never hidden. */
    includeInactive: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
// ---------------------------------------------------------------------------
// Pricing agreements
// ---------------------------------------------------------------------------
const datesAreOrdered = (value) => !value.validFrom ||
    !value.validUntil ||
    value.validUntil >= value.validFrom;
const agreementCoreSchema = z.object({
    /** The laboratory's own or the client's reference. Agreements are not
     *  numbered: no numbering type was approved for them (decision D39's rule
     *  applied consistently). */
    reference: optionalText(64),
    title: optionalBusinessText(500),
    validFrom: isoDateSchema,
    /** Null is "until cancelled", which is how most agreements are written. */
    validUntil: isoDateSchema.nullable().optional(),
    notes: optionalText(4000),
});
export const createPricingAgreementSchema = agreementCoreSchema
    .extend({ clientId: uuidSchema })
    .refine(datesAreOrdered, {
    message: 'The end date may not precede the start date',
    path: ['validUntil'],
});
export const updatePricingAgreementSchema = agreementCoreSchema
    .partial()
    .extend({
    isActive: z.boolean().optional(),
    reason: reasonSchema.optional(),
})
    .refine(datesAreOrdered, {
    message: 'The end date may not precede the start date',
    path: ['validUntil'],
});
/**
 * One agreed price.
 *
 * `agreedPriceMinor` may be zero here, and that is a real thing: a laboratory
 * genuinely does agree to perform one service at no charge as part of a
 * package. The distinction from the catalogue is exact — a catalogue base
 * price of zero would assert a price list nobody wrote, while an AGREED zero
 * is something two parties signed.
 */
export const pricingAgreementLineSchema = z.object({
    catalogueItemId: uuidSchema,
    agreedPriceMinor: moneyMinorSchema,
    notes: optionalText(1000),
});
export const setPricingAgreementLinesSchema = z.object({
    lines: z.array(pricingAgreementLineSchema).max(500),
    reason: reasonSchema.optional(),
});
export const listPricingAgreementsQuerySchema = listQuerySchema.extend({
    clientId: uuidSchema.optional(),
    includeInactive: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
});
// ---------------------------------------------------------------------------
// Quotations
// ---------------------------------------------------------------------------
/**
 * A discount is a PERCENTAGE OR AN AMOUNT, never both.
 *
 * Both together is not a richer discount, it is an ambiguous one: nobody could
 * say from the record whether the amount was applied before or after the
 * percentage, and the client's copy and ours could disagree.
 */
const discountShape = {
    discountPercent: z.number().min(0).max(100).nullable().optional(),
    discountAmountMinor: moneyMinorSchema.nullable().optional(),
};
const discountIsUnambiguous = (value) => value.discountPercent === undefined ||
    value.discountPercent === null ||
    value.discountAmountMinor === undefined ||
    value.discountAmountMinor === null;
const DISCOUNT_REFINEMENT = {
    message: 'A discount is either a percentage or an amount, never both',
    path: ['discountAmountMinor'],
};
/**
 * The details of a client who has NO client record.
 *
 * A quotation for somebody who walked in this morning is an ordinary event,
 * and refusing to write one until a client file exists would put paperwork
 * ahead of the work. These fields are the same ones a client file holds, so
 * conversion on acceptance copies rather than re-asks (Phase 5 acceptance
 * criteria).
 */
export const prospectSchema = z.object({
    name: businessTextSchema,
    contactName: optionalText(200),
    /** The contact's job title, printed beside the name (D200). */
    contactTitle: optionalText(120),
    contactEmail: z.string().trim().toLowerCase().email().max(320).nullable().optional(),
    contactPhone: z.string().trim().min(5).max(32).nullable().optional(),
    vatRegistrationNumber: optionalText(50),
    commercialRegistrationNumber: optionalText(50),
    addressLine: optionalBusinessText(500),
    city: optionalBusinessText(200),
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .length(2)
        .regex(/^[A-Z]{2}$/)
        .nullable()
        .optional(),
});
/**
 * One line of a quotation.
 *
 * `unitPriceMinor` is OPTIONAL on input. Left out, the server resolves the
 * price itself — the client's agreement first, then the catalogue base price —
 * and records where it came from. Supplied and different from the resolved
 * price, it is an OVERRIDE: it requires `pricing.override` and a reason, and
 * the API refuses it without either.
 */
export const quotationLineSchema = z
    .object({
    catalogueItemId: uuidSchema.nullable().optional(),
    /**
     * Required when there is no catalogue entry — a mobilisation charge or a
     * site visit is a legitimate line with nothing in the catalogue behind it,
     * and it still has to say what it is.
     */
    description: bilingualTextSchema.optional(),
    quantity: z
        .number()
        .positive()
        .max(1_000_000)
        // Three decimals is enough for half a day or a quarter of an hour, and
        // stops a float with fifteen digits of noise reaching the arithmetic.
        .refine((value) => Number.isInteger(value * 1000), {
        message: 'A quantity may carry at most three decimal places',
    }),
    unitPriceMinor: moneyMinorSchema.optional(),
    priceOverrideReason: reasonSchema.optional(),
    ...discountShape,
    notes: optionalText(1000),
})
    .refine((line) => Boolean(line.catalogueItemId) || Boolean(line.description), {
    message: 'A line names a catalogue entry or describes itself',
    path: ['description'],
})
    .refine(discountIsUnambiguous, DISCOUNT_REFINEMENT);
/**
 * What a quotation PRINTS beyond its lines (D200). Each is this quotation's
 * own; the three wordings are copied from the laboratory's settings when the
 * quotation is created and may be edited while it is a draft. The contact and
 * the project location are chosen from records on file, never typed again.
 */
const quotationDocumentShape = {
    /** How the client asked: "By phone", an e-mail reference, a tender number. */
    inquiryReference: optionalBusinessText(200),
    /** A contact person of the quotation's client. */
    contactId: uuidSchema.nullable().optional(),
    /** A location of the quotation's project. */
    projectLocationId: uuidSchema.nullable().optional(),
    introduction: documentWordingSchema(2000).optional(),
    paymentTerms: documentWordingSchema(1000).optional(),
    /** General conditions. */
    terms: documentWordingSchema(4000).optional(),
    scopeOfWork: optionalBusinessText(4000),
    remarks: optionalBusinessText(2000),
    /** Printed for the client — unlike `notes`, which are internal. */
    additionalNotes: optionalBusinessText(2000),
};
export const createQuotationSchema = z
    .object({
    /** Exactly one of these. A quotation is for a client OR for a prospect. */
    clientId: uuidSchema.nullable().optional(),
    prospect: prospectSchema.nullable().optional(),
    projectId: uuidSchema.nullable().optional(),
    /**
     * Optional. Left out, the server applies this laboratory's own default
     * validity period from its settings — the interface never restates the
     * default (CLAUDE.md section 6.1).
     */
    validUntil: isoDateSchema.optional(),
    issueDate: isoDateSchema.optional(),
    subject: optionalBusinessText(500),
    ...quotationDocumentShape,
    /** INTERNAL notes: never printed on the document (D200). */
    notes: optionalText(4000),
    ...discountShape,
    discountReason: reasonSchema.optional(),
    lines: z.array(quotationLineSchema).max(200).optional(),
})
    .refine((value) => Boolean(value.clientId) !== Boolean(value.prospect), {
    message: 'A quotation is for a registered client or for a prospect, not both and not neither',
    path: ['clientId'],
})
    .refine(discountIsUnambiguous, DISCOUNT_REFINEMENT);
/**
 * Editing a quotation (D208). On a DRAFT everything here may change, including
 * who it is addressed to: a `clientId` addresses it to a client on file, a
 * `prospect` to somebody with no file yet — never both. Once the quotation has
 * left draft only `notes` (internal, never printed) still saves; the API
 * refuses everything else, the validity date included.
 */
export const updateQuotationSchema = z
    .object({
    clientId: uuidSchema.nullable().optional(),
    projectId: uuidSchema.nullable().optional(),
    prospect: prospectSchema.nullable().optional(),
    validUntil: isoDateSchema.optional(),
    subject: optionalBusinessText(500),
    ...quotationDocumentShape,
    notes: optionalText(4000),
    ...discountShape,
    /** `null` clears it, with the discount it explained. */
    discountReason: reasonSchema.nullable().optional(),
    reason: reasonSchema.optional(),
})
    .refine((value) => !(value.clientId && value.prospect), {
    message: 'A quotation is for a registered client or for a prospect, not both',
    path: ['clientId'],
})
    .refine(discountIsUnambiguous, DISCOUNT_REFINEMENT);
export const setQuotationLinesSchema = z.object({
    lines: z.array(quotationLineSchema).max(200),
    reason: reasonSchema.optional(),
});
/**
 * Recording that the quotation was sent.
 *
 * THIS PRODUCT SENDS NOTHING. There is no mail transport in it, and there will
 * not be one before Phase 12. What this records is that a PERSON put the
 * document in the client's hands, and how. Presenting it as an electronic send
 * would be exactly the dishonest display CLAUDE.md section 8 forbids: a send
 * result must be the actual result.
 */
export const sendQuotationSchema = z.object({
    channel: quotationSendChannelSchema,
    sentAt: z.string().datetime({ offset: true }).optional(),
    note: optionalText(1000),
});
/**
 * The client's ACTUAL answer.
 *
 * There is no default and no optional outcome. Somebody has to state what the
 * client said, because nothing else in the system can know it — and a record
 * never advances because a button was pressed (CLAUDE.md section 8).
 */
export const recordQuotationResponseSchema = z.object({
    outcome: z.enum(['accepted', 'rejected']),
    respondedAt: z.string().datetime({ offset: true }).optional(),
    /** Required for a rejection: "why not" is the first question anyone asks. */
    note: optionalText(2000),
    /**
     * Convert the prospect into a client file in the same act.
     *
     * Optional. When it is absent the acceptance is still recorded — refusing to
     * record a client's real answer because a duplicate search has not been run
     * would put the paperwork ahead of the fact. The quotation then reports that
     * conversion is still outstanding, and the conversion endpoint completes it.
     */
    convertProspect: z
        .object({
        duplicateSearchToken: z.string().min(1).max(2048),
        acknowledgedDuplicateIds: z.array(uuidSchema).max(50).optional(),
        duplicateAcknowledgementNote: optionalText(1000),
        /** Link to an EXISTING client instead of creating one, when the desk
         *  recognises the prospect as somebody already on file. */
        existingClientId: uuidSchema.optional(),
    })
        .optional(),
});
export const convertQuotationProspectSchema = z.object({
    duplicateSearchToken: z.string().min(1).max(2048),
    acknowledgedDuplicateIds: z.array(uuidSchema).max(50).optional(),
    duplicateAcknowledgementNote: optionalText(1000),
    existingClientId: uuidSchema.optional(),
});
export const listQuotationsQuerySchema = listQuerySchema.extend({
    clientId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    status: quotationStoredStatusSchema.optional(),
    /**
     * Filter on the DERIVED state as well, since "show me everything that has
     * lapsed" is the question a desk actually asks.
     */
    expired: z.enum(['true', 'false']).optional(),
    /** Quotations written for somebody with no client file yet. */
    prospectsOnly: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /**
     * Deleted (rejected) quotations are hidden by default; their rows never
     * leave. Asking for them needs `quotations.delete` (D210, D214).
     */
    includeDeleted: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
/** Soft-deleting a REJECTED quotation (D210). The reason is mandatory. */
export const deleteQuotationSchema = z.object({
    reason: reasonSchema,
});
/** Recording that a copy was printed. It changes no status whatsoever. */
/**
 * The quotation document (D198): the language to print it in, and whether the
 * browser should save the file (`download=1`) rather than show it.
 */
export const quotationDocumentQuerySchema = z.object({
    language: z.enum(['ar', 'en']),
    download: z
        .enum(['0', '1'])
        .optional()
        .transform((value) => value === '1'),
});
export const recordQuotationPrintSchema = z.object({
    locale: z.enum(ALL_LOCALES).optional(),
    note: optionalText(500),
});
// ---------------------------------------------------------------------------
// The laboratory's own quotation settings
// ---------------------------------------------------------------------------
/**
 * Settings, not literals.
 *
 * A default validity period and the standard terms paragraph printed at the
 * foot of every quotation are exactly the organisation-specific values
 * CLAUDE.md section 6.1 keeps out of the source. `30` is the product's default
 * for a NEW laboratory, applied in one place in the backend; every laboratory
 * edits its own.
 */
export const quotationSettingsSchema = z.object({
    quotationValidityDays: z.number().int().positive().max(3650).optional(),
    quotationTerms: documentWordingSchema(4000).optional(),
});
//# sourceMappingURL=catalogue.js.map